// Recovers the Apple ID token from a Firebase sign-in that failed because the
// matched account already holds an apple.com provider.
//
// WHY THIS EXISTS, AND WHY IT IS A WORKAROUND
//
// Sign in with Apple issues a subject identifier scoped to the Apple developer
// team, and our apps span two teams. When an identity minted under the other
// team collides with an existing account on its email address, Identity
// Platform answers `accounts:signInWithIdp` with HTTP 200 and
// `errorMessage: "PROVIDER_ALREADY_LINKED"` — it tried to link apple.com onto
// the matched account and found that slot already taken. That same response
// body carries `oauthIdToken`: precisely the Apple ID token our resolver needs
// in order to work out which account the person really is.
//
// The Firebase SDK attaches the response to the thrown error for exactly three
// cases — `needConfirmation`, `FEDERATED_USER_ID_ALREADY_LINKED` and
// `EMAIL_EXISTS`. `PROVIDER_ALREADY_LINKED` is not among them, so it takes the
// generic error path and the token is discarded before any public API can see
// it: `OAuthProvider.credentialFromError()` returns null. There is no supported
// way to reach it.
//
// So we read the response as it goes past. The durable fix is to run the Apple
// OAuth flow ourselves and stop inferring identity from Firebase error codes at
// all; this buys that time without touching the sign-in path everyone else uses.
//
// TWO PROPERTIES MAKE THE LOOSE COUPLING ACCEPTABLE
//
// It cannot fail open. If Google changes the response shape, no token is
// captured, the caller rethrows the user's original error, and behaviour is
// exactly what it is today — the feature stops working, it does not misbehave.
//
// It is not a trust boundary. The captured token is never believed here: the
// resolver verifies it against Apple's JWKS — issuer, audience, expiry and
// email_verified — before it will mint anything. A wrong or tampered capture
// yields a 401, not a session.

/** Path fragment identifying the Identity Platform call that carries the token. */
const SIGN_IN_WITH_IDP_PATH = '/v1/accounts:signInWithIdp'

export interface AppleIdTokenCapture {
  /**
   * The Apple ID token from a failed exchange, if one went past. Read this
   * before calling `stop()`; the capture is meant to live only for the
   * duration of one sign-in attempt.
   */
  idToken(): string | undefined
  /** Restores the original `fetch`. Safe to call more than once. */
  stop(): void
}

// String() rather than instanceof branches: a URL from another realm fails
// instanceof and would fall through to `.url`, yielding undefined and throwing.
// String() handles string, URL and Request alike and cannot throw.
function requestUrl(input: RequestInfo | URL): string {
  return typeof input === 'string' ? input : String(input instanceof Request ? input.url : input)
}

/**
 * Instruments `fetch` for the duration of one Apple sign-in attempt.
 *
 * Always pair this with `stop()` in a `finally`: leaving the wrapper installed
 * would make every later request pay for a URL comparison, and would keep this
 * closure — and a credential inside it — alive.
 */
export function beginAppleIdTokenCapture(): AppleIdTokenCapture {
  // No fetch to wrap (SSR, or a test environment without one). The caller then
  // gets no token and rethrows, which is the correct fallback.
  if (typeof globalThis.fetch !== 'function') {
    return { idToken: () => undefined, stop: () => {} }
  }

  const original = globalThis.fetch
  let captured: string | undefined
  let stopped = false

  const patched: typeof globalThis.fetch = async (input, init) => {
    const response = await original(input, init)
    if (stopped) return response
    try {
      if (requestUrl(input).includes(SIGN_IN_WITH_IDP_PATH)) {
        // Clone first — the SDK still has to consume the real body.
        const body = (await response.clone().json()) as {
          errorMessage?: unknown
          oauthIdToken?: unknown
        }
        // Only retain a token from a FAILED exchange. On success the SDK
        // establishes the session itself and we have no business holding a
        // credential we will not use.
        if (
          typeof body.errorMessage === 'string' &&
          typeof body.oauthIdToken === 'string' &&
          body.oauthIdToken !== ''
        ) {
          captured = body.oauthIdToken
        }
      }
    } catch {
      // A non-JSON body, a stream that cannot be cloned, a parse failure — all
      // mean the same thing here: no token. This must never break a sign-in,
      // and the caller's fallback of rethrowing the original error is correct.
    }
    return response
  }

  globalThis.fetch = patched

  return {
    idToken: () => captured,
    stop: () => {
      stopped = true
      // Release the credential. It matters most in the case just below, where
      // the wrapper cannot be removed and this closure therefore outlives the
      // sign-in attempt.
      captured = undefined
      // Only restore if nothing else wrapped fetch after us; clobbering
      // someone else's wrapper would be worse than leaving ours in place.
      if (globalThis.fetch === patched) globalThis.fetch = original
    },
  }
}
